import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion'; 
import Sidebar from './Sidebar';
import MagneticButton from './MagneticButton'; 

const MagneticWrapper = ({ children, className }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const springConfig = { stiffness: 150, damping: 15, mass: 0.1 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  const handleMouseMove = (e) => {
    if (!ref.current || window.innerWidth < 768) return; 
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    x.set((clientX - centerX) * 0.2); 
    y.set((clientY - centerY) * 0.2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: springX, y: springY }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Navbar() {
  const location = useLocation();
  const textColor = 'text-black'; 
  const dotColor = 'bg-black';
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredPath, setHoveredPath] = useState(null);
  
  // 1. Track if we are on a mobile device
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 200);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const isDotVisible = (path) => {
    if (hoveredPath === path) return true; 
    if (hoveredPath === null && location.pathname === path) return true; 
    return false; 
  };

  // 2. Logic: Show the floating circle button if on mobile OR if scrolled down on desktop
  const showFloatingButton = isMobile || isScrolled;

  return (
    <>
      {/* Navbar Container */}
      {/* 3. Removed the plain hamburger menu block completely from inside this <nav> */}
      {/* Changed py-6 to pt-14 pb-6 strictly for mobile alignment */}
<nav className={`w-full bg-transparent px-4 md:px-24 pt-14 pb-6 md:py-10 flex items-center justify-between font-['Montserrat',sans-serif] z-30 absolute top-0 left-0`}>
        <div className="flex items-center gap-24">
          <MagneticWrapper className="inline-block">
            <Link to="/" className={`text-lg md:text-2xl [letter-spacing:-1px] font-regular no-underline ${textColor} block`}>
              Deepayu Ninama
            </Link>
          </MagneticWrapper>
        </div>
        
        {/* Desktop Links */}
        <ul className={`hidden md:flex items-center gap-10 uppercase text-base font-medium ${textColor}`}>
          <li className="group" onMouseEnter={() => setHoveredPath('/')} onMouseLeave={() => setHoveredPath(null)}>
            <MagneticWrapper className="inline-block">
              <Link to="/" className="relative transition-colors block">
                Who Am I ?
                <span className={`absolute -bottom-6 left-1/2 -translate-x-1/2 w-2 h-2 ${dotColor} rounded-full transition-all duration-300 ${isDotVisible('/') ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
              </Link>
            </MagneticWrapper>
          </li>
          <li className="group" onMouseEnter={() => setHoveredPath('/work')} onMouseLeave={() => setHoveredPath(null)}>
            <MagneticWrapper className="inline-block">
              <Link to="/work" className="relative transition-colors block">
                Work
                <span className={`absolute -bottom-6 left-1/2 -translate-x-1/2 w-2 h-2 ${dotColor} rounded-full transition-all duration-300 ${isDotVisible('/work') ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
              </Link>
            </MagneticWrapper>
          </li>
        </ul>
      </nav>

      {/* Floating Circular Menu Button */}
      <AnimatePresence>
        {showFloatingButton && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-10 right-4 md:right-24 z-50"
          >
            <MagneticButton
              onClick={toggleMenu}
              className="text-white bg-black p-5 md:p-8 rounded-full hover:bg-indigo-600 transition-colors duration-300"
              ariaLabel="Open menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </MagneticButton>
          </motion.div>
        )}
      </AnimatePresence>

      <Sidebar isMenuOpen={isMenuOpen} closeMenu={closeMenu} toggleMenu={toggleMenu} />
    </>
  );
}